// ============================================================================
// UsageLimitBanner — proactive limit heads-up (ORG-USAGE-2026)
//
// The owner directive behind this feature: a member must never be surprised by
// a pause. This app-level strip appears once a member crosses 80% of any
// configured limit, says exactly which limit and when it refreshes, and links
// to the detail. At 100% it explains the situation and what to do — it never
// just stops working with no explanation.
//
// Renders nothing for: owners, members with no limits (the default), accounts
// that predate the feature, and anyone below the warning threshold. Dismissal
// is per-session and per-severity, so crossing into a worse state re-surfaces
// it once.
//
// Rendering (2026-08-14): registers with the surrounding GlobalBannerCenter
// (the ONE global top strip; rotates when several notices are eligible) and
// renders nothing itself. Standalone (no BannerBusContext above), it falls
// back to rendering its legacy strip directly.
// ============================================================================

import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getMyUsage, type MyUsageLimit } from '../../services/rbacService';
import { subscribeUsageWarnings } from '../../services/prest0nSession/usageWarningChannel';
import { useBannerSurface } from '../common/bannerSurface';
import { BannerBusContext, useRegisterBanner } from '../common/GlobalBannerCenter';
import { ACTIVITY_COLORS, formatResetTime, formatUsd } from './activityTheme';

/**
 * "$8.50 of $10.00" for $-based limits (dollars are not tokens). Token-metered
 * caps return null — raw token counts never display (2026-08-13 WU mandate);
 * the percentage in the banner copy already says where the member stands.
 * Display only — enforcement is untouched.
 */
function usedOfLimit(l: MyUsageLimit): string | null {
  return l.metric === 'usd' ? `${formatUsd(l.used)} of ${formatUsd(l.limit)}` : null;
}

const Strip = styled.div<{ $severe: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding: 0.625rem 1rem;
  background: ${(p) => (p.$severe ? 'rgba(153, 27, 27, 0.07)' : 'rgba(184, 134, 11, 0.08)')};
  border-bottom: 1px solid
    ${(p) => (p.$severe ? 'rgba(153, 27, 27, 0.2)' : 'rgba(184, 134, 11, 0.2)')};
  color: ${(p) => (p.$severe ? ACTIVITY_COLORS.severity.reached : '#7a5c08')};
  font-size: 0.8125rem;
  line-height: 1.45;
`;

const Message = styled.span`
  text-align: center;
`;

const DetailLink = styled(Link)`
  color: inherit;
  font-weight: 600;
  text-decoration: underline;
  white-space: nowrap;
`;

const Dismiss = styled.button`
  background: none;
  border: none;
  color: inherit;
  opacity: 0.7;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0 0.25rem;

  &:hover {
    opacity: 1;
  }
`;

const DISMISS_KEY = 'prest0n_usagelimit_dismissed';

function pickMostUrgent(limits: MyUsageLimit[]): MyUsageLimit | null {
  const relevant = limits.filter((l) => l.level && l.level !== 'none');
  if (relevant.length === 0) return null;
  return relevant.reduce((a, b) => (b.ratio > a.ratio ? b : a));
}

/** Navigation-triggered refreshes are debounced to this interval; pushed turn
 *  warnings (subscribeUsageWarnings) always refresh immediately. */
const NAV_REFRESH_TTL_MS = 30_000;

const UsageLimitBanner: React.FC = () => {
  const bus = useContext(BannerBusContext);
  const location = useLocation();
  // Banner-surface gate (2026-08-31 consistency directive): keyed on the
  // VIEWER, not on a route allowlist, so the meter speaks on every page a
  // signed-in member visits — home included — and on none for a visitor.
  const onSurface = useBannerSurface();
  const lastLoadRef = React.useRef(0);
  const [limits, setLimits] = useState<MyUsageLimit[]>([]);
  const [dismissedKey, setDismissedKey] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY);
    } catch {
      return null;
    }
  });

  const load = useCallback(async () => {
    lastLoadRef.current = Date.now();
    try {
      const res = await getMyUsage();
      // A workspace whose plan means limits aren't applied must never see a
      // "you've reached your limit" strip — nothing is going to block.
      setLimits((res as any)?.enforced === false ? [] : res?.limits ?? []);
    } catch {
      // Fail quiet: a banner that can't load its data simply doesn't render.
      setLimits([]);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let unsub: (() => void) | undefined;
    try {
      unsub = onAuthStateChanged(getAuth(), (user) => {
        if (cancelled) return;
        if (user) load();
        else setLimits([]);
      });
    } catch {
      /* auth unavailable in embedded hosts — no banner */
    }
    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, [load]);

  // Refresh when the member navigates between app surfaces, so the meter stays
  // roughly live without polling — debounced so route-hopping doesn't invoke a
  // Cloud Function per click (read-amplification audit finding).
  useEffect(() => {
    if (!onSurface) return;
    if (Date.now() - lastLoadRef.current < NAV_REFRESH_TTL_MS) return;
    load();
  }, [location.pathname, onSurface, load]);

  // ORG-USAGE-2026: the turn pipeline pushes the gate's ≥80% warning here the
  // moment it happens, so a member chatting continuously on one route sees the
  // strip mid-conversation instead of only after their next navigation.
  useEffect(() => subscribeUsageWarnings(() => { void load(); }), [load]);


  const urgent = onSurface ? pickMostUrgent(limits) : null;

  const severe = urgent ? urgent.level === 'reached' || urgent.level === 'critical' : false;
  const stateKey = urgent ? `${urgent.id}:${urgent.level}` : null;
  const visible = urgent !== null && dismissedKey !== stateKey;

  const handleDismiss = () => {
    if (!stateKey) return;
    setDismissedKey(stateKey);
    try {
      sessionStorage.setItem(DISMISS_KEY, stateKey);
    } catch {
      /* ignore */
    }
  };

  // Mirror the server's warning copy guard: a member who is NOT blocked must
  // never read "you've used 100%" — cap the displayed percentage at 99 and
  // floor (not round) so display always agrees with the block state.
  const pct = Math.min(99, Math.floor((urgent?.ratio ?? 0) * 100));
  // Dollar amounts for $-caps; null for token-metered caps, whose parenthetical
  // is omitted — the percentage carries the message without token numbers.
  const detail = urgent ? usedOfLimit(urgent) : null;

  const content = urgent ? (
    <>
      <Message>
        {urgent.exceeded ? (
          <>
            you've reached your {urgent.label} usage limit{detail ? ` (${detail})` : ''}. it
            refreshes {formatResetTime(urgent.resetMs)} — ask an account owner or admin if you
            need more sooner.
          </>
        ) : (
          <>
            you've used {pct}% of your {urgent.label} usage limit
            {detail ? ` (${detail})` : ''}. it refreshes {formatResetTime(urgent.resetMs)}.
          </>
        )}
      </Message>
      <DetailLink to="/activity">view details</DetailLink>
    </>
  ) : null;

  // At 100% (blocked / critical) the notice escalates to critical tone.
  useRegisterBanner(
    visible
      ? {
          id: 'usage-limit',
          tone: severe ? 'critical' : 'warn',
          content,
          dismissible: true,
          onDismiss: handleDismiss,
        }
      : null,
  );

  // Inside a GlobalBannerCenter the center owns the strip.
  if (bus) return null;

  // ── Legacy standalone fallback (no center above) ──────────────────────────
  if (!visible) return null;

  return (
    <Strip $severe={severe} role="status">
      {content}
      <Dismiss aria-label="dismiss" onClick={handleDismiss}>
        ×
      </Dismiss>
    </Strip>
  );
};

export default UsageLimitBanner;
